import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Link, Route, Routes, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./SignUp.scss";
import SignUpContextProvider from "../../context/SignUpContext";
import { SubscriptionPlan } from "./SignUpApi";
import loadable from "@loadable/component";
import { useStickyState } from "../../hooks/useStickyState";
import { TrustPilotBox } from "../common/TrustPilotBox";
import { SignUpChangePaymentDate } from "./SignUpChangePaymentDate";
import { isCountryUs } from "../../utils/country";

const StepProgressBar = loadable(
  () => import(/* webpackChunkName: "StepProgressBar" */ "../ui/StepProgressBar")
) as any;
const SignUpLegalName = loadable(() => import(/* webpackChunkName: "SignUpLegalName" */ "./SignUpLegalName")) as any;
const SignUpEmailUS = loadable(() => import(/* webpackChunkName: "SignUpEmailUS" */ "./contact/SignUpEmailUS")) as any;
const SignUpAddress = loadable(() => import(/* webpackChunkName: "SignUpAddress" */ "./address/SignUpAddress")) as any;
const LoginSignupSwitch = loadable(
  () => import(/* webpackChunkName: "LoginSignupSwitch" */ "../common/LoginSignupSwitch"),
  {
    resolveComponent: (components) => components.LoginSignupSwitch,
  }
) as any;
const SignUpPayment = loadable(() => import(/* webpackChunkName: "SignUpPayment" */ "./SignUpPayment")) as any;
const SignUpConfirmation = loadable(
  () => import(/* webpackChunkName: "SignUpConfirmation" */ "./SignUpConfirmation")
) as any;
const SignUpContactInfo = loadable(
  () => import(/* webpackChunkName: "SignUpContactInfo" */ "./contact/SignUpContactInfo")
) as any;
const SignUpCreatePassword = loadable(
  () => import(/* webpackChunkName: "SignUpCreatePassword" */ "./password/SignUpCreatePassword")
) as any;
const SignUpReferrals = loadable(
  () => import(/* webpackChunkName: "SignUpReferrals" */ "./referrals/SignUpReferrals")
) as any;
const SignUpAboutYourData = loadable(
  () => import(/* webpackChunkName: "SignUpAboutYourData" */ "./aboutyourdata/SignUpAboutYourData")
) as any;
const SignUpSchedulePayment = loadable(
  () => import(/* webpackChunkName: "SignUpSchedulePayment" */ "./schedulepayment/SignUpSchedulePayment")
) as any;
const SignUpSubscription = loadable(
  () => import(/* webpackChunkName: "SignUpSubscription" */ "./plans/SignUpSubscription")
) as any;

export const SIGNUP_LEGALNAME_URL = "/signup";
export const SIGNUP_ADDRESS_URL = "/signup/address";
export const SIGNUP_CONTACTINFO_URL = "/signup/contactinfo";
export const CREATE_PASSWORD_URL = "/signup/password";
export const SUBSCRIPTION_PLANS_URL = "/signup/subscription";
/* The US asks for the email first, on a screen of its own like the UK flow,
   so the name step moves off /signup to make room for it. */
export const SIGNUP_US_EMAIL_URL = "/signup";
export const SIGNUP_US_LEGALNAME_URL = "/signup/legalname";

/* Below the legal name's 0 because it comes before it: step numbers are
   compared by order, e.g. only steps under SUBSCRIPTIONS_STEP_NO get the
   progress bar. */
export const EMAIL_STEP_NO = -1;
export const LEGAL_NAME_STEP_NO = 0;
export const ADDRESS_STEP_NO = 1;
export const CONTACT_INFO_STEP_NO = 2;
export const CREATE_PASSWORD_STEP_NO = 3;
export const REFERRALS_STEP_NO = 4;
export const ABOUT_YOUR_DATA_STEP_NO = 5;
export const LOADING_CREDIT_FILE_STEP_NO = 999;
export const SUBSCRIPTIONS_STEP_NO = 6;
export const PAYMENT_STEP_NO = 8;
export const CHANGE_PAYMENT_DATE_STEP_NO = 9;
export const CONFIRMATION_STEP_NO = 10;
export const SCHEDULE_CONFIRMATION_STEP_NO = 11;
export const SIGN_UP_REFERRALS_URL = "/signup/referrals";
export const SIGN_UP_ABOUT_YOUR_DATA_URL = "/signup/aboutyourdata";
export const SIGN_UP_PAYMENT_URL = "/signup/payment";
export const SIGN_UP_CHANGE_PAYMENT_DATE_URL = "/signup/paymentdate";
export const SIGNUP_CONFIRMATION_URL = "/signup/confirmation";
export const SIGNUP_SCHEDULE_URL = "/signup/schedule";

/* "How did you hear about us?" is a UK-only question now — the US does not
   ask it, so that step is absent from the US flow entirely: no route, no
   dot on the progress bar, and the password step goes straight on to the
   data consent. The UK still falls back to this flow when the V2 flag is
   off, which is why this is a country check rather than a deletion. */
export const isReferralsStepEnabled = (): boolean => !isCountryUs();

/** The step the flow opens on: no back button there, and the log-in switch. */
const getStartStepNo = (): number => (isCountryUs() ? EMAIL_STEP_NO : LEGAL_NAME_STEP_NO);

interface ISignUp {
  setHeaderClass: (headerClass: string | null) => void;
  setWrapperClass: (wrapperClass: string | null) => void;
  setBypassAuthCheck: (bypassAuthCheck: boolean) => void;
  setDisplayBackButton: (displayBackButton: boolean) => void;
}

interface IUseSignUp {
  layout: string;
  subscriptionPlan: SubscriptionPlan | undefined;
  signUpStep: number;
  setSignUpStep: (signUpStep: number) => void;
  setSubscriptionPlan: (subscriptionPlan: SubscriptionPlan | undefined) => void;
}

const useSignUp = ({ setHeaderClass, setWrapperClass, setDisplayBackButton }: any): IUseSignUp => {
  const [signUpStep, setSignUpStep] = useStickyState<number>(0, "signUpStep");
  const [styleClasses, setStyleClasses] = useState<any>({
    headerClass: "",
    wrapperClass: "",
    layout: "welcome-layout",
  });
  const [subscriptionPlan, setSubscriptionPlan] = useStickyState<SubscriptionPlan | undefined>(
    undefined,
    "subscriptionPlan"
  );

  useEffect(() => {
    const styleClasses: any = getClassNames();
    setStyleClasses(styleClasses);
    const { headerClass, wrapperClass } = styleClasses;
    setHeaderClass(headerClass);
    setWrapperClass(wrapperClass);
    const displayBackButton: boolean =
      signUpStep > getStartStepNo() &&
      signUpStep !== SUBSCRIPTIONS_STEP_NO &&
      signUpStep !== SCHEDULE_CONFIRMATION_STEP_NO &&
      signUpStep !== CHANGE_PAYMENT_DATE_STEP_NO;
    setDisplayBackButton(displayBackButton);
  }, [signUpStep]);

  const getClassNames = (): any => {
    switch (signUpStep) {
      case SUBSCRIPTIONS_STEP_NO:
        return { headerClass: "", wrapperClass: "unauthorized-white-layout", layout: "subscriptions-layout" };
      case LOADING_CREDIT_FILE_STEP_NO:
        return { headerClass: "d-none", wrapperClass: "", layout: "welcome-layout welcome-loading-layout" };
      default:
        return { headerClass: "", wrapperClass: "", layout: "welcome-layout" };
    }
  };

  const layout: string = styleClasses?.layout || "welcome-layout";

  return {
    layout,
    subscriptionPlan,
    signUpStep,
    setSignUpStep,
    setSubscriptionPlan,
  };
};

export const SignUp = ({ setHeaderClass, setWrapperClass, setBypassAuthCheck, setDisplayBackButton }: ISignUp) => {
  /* The steps the bar counts, in the order they are walked. Deriving both
     its length and the current position from one list is what stops the
     total and the highlighted dot disagreeing when a step is dropped. */
  const progressSteps: number[] = [
    ...(isCountryUs() ? [EMAIL_STEP_NO] : []),
    LEGAL_NAME_STEP_NO,
    ADDRESS_STEP_NO,
    CONTACT_INFO_STEP_NO,
    CREATE_PASSWORD_STEP_NO,
    ...(isReferralsStepEnabled() ? [REFERRALS_STEP_NO] : []),
    ABOUT_YOUR_DATA_STEP_NO,
  ];
  const START_SIGNUP_STEP_NO = getStartStepNo();
  const { layout, signUpStep, subscriptionPlan, setSignUpStep, setSubscriptionPlan } = useSignUp({
    setHeaderClass,
    setWrapperClass,
    setDisplayBackButton,
  });

  const { t } = useTranslation();
  const outletContext = (useOutletContext as any)?.() || {};
  const { displayBackButton: contextDisplayBack, backButtonRoute } = outletContext;

  const getTargetBackRoute = (): string => {
    if (backButtonRoute && backButtonRoute !== "#") {
      return backButtonRoute;
    }
    switch (signUpStep) {
      case LEGAL_NAME_STEP_NO:
        // Only reached in the US: elsewhere the name step is the first one and has no back button
        return SIGNUP_US_EMAIL_URL;
      case ADDRESS_STEP_NO:
        return isCountryUs() ? SIGNUP_US_LEGALNAME_URL : SIGNUP_LEGALNAME_URL;
      case CONTACT_INFO_STEP_NO:
        return SIGNUP_ADDRESS_URL;
      case CREATE_PASSWORD_STEP_NO:
        return SIGNUP_CONTACTINFO_URL;
      case REFERRALS_STEP_NO:
        return CREATE_PASSWORD_URL;
      case ABOUT_YOUR_DATA_STEP_NO:
        return isReferralsStepEnabled() ? SIGN_UP_REFERRALS_URL : CREATE_PASSWORD_URL;
      case SUBSCRIPTIONS_STEP_NO:
        return SIGN_UP_ABOUT_YOUR_DATA_URL;
      case PAYMENT_STEP_NO:
        return SUBSCRIPTION_PLANS_URL;
      default:
        return SIGNUP_LEGALNAME_URL;
    }
  };

  const showBackButton =
    contextDisplayBack ??
    (signUpStep > START_SIGNUP_STEP_NO &&
      signUpStep !== SUBSCRIPTIONS_STEP_NO &&
      signUpStep !== SCHEDULE_CONFIRMATION_STEP_NO &&
      signUpStep !== CHANGE_PAYMENT_DATE_STEP_NO);

  const renderLoginSignUpSwitch = (): JSX.Element | null => {
    if (signUpStep > START_SIGNUP_STEP_NO) {
      return null;
    }
    return (
      /* See the note on the same block in `SignUpV2`. */
      <div className="signupSwitchRow">
        <LoginSignupSwitch isSignup={true} />
      </div>
    );
  };

  return (
    <SignUpContextProvider>
      <div className={layout}>
        {signUpStep < SUBSCRIPTIONS_STEP_NO ? (
          <StepProgressBar
            steps={[...new Array(progressSteps.length)]}
            signUpStep={Math.max(0, progressSteps.indexOf(signUpStep))}
            backRoute={showBackButton ? getTargetBackRoute() : undefined}
          />
        ) : showBackButton ? (
          <div className="signup-standalone-back-bar">
            <Link
              to={getTargetBackRoute()}
              className="stepper-back-btn"
              id="stepper-back-button"
              aria-label={t("common.back")}
            >
              <i className="bi bi-arrow-left" />
              <span>{t("common.back")}</span>
            </Link>
          </div>
        ) : null}
        <Container className="pb-3">
          {/* Under the step, not above it: the form is what the screen is for,
              and this is where sign-in puts the same control. */}
          <Row className="justify-content-center pt-2">
            <Col>
              <Routes>
                <Route
                  index
                  element={
                    isCountryUs() ? (
                      <SignUpEmailUS setSignUpStep={setSignUpStep} />
                    ) : (
                      <SignUpLegalName setSignUpStep={setSignUpStep} />
                    )
                  }
                />
                {isCountryUs() && (
                  <Route path="legalname" element={<SignUpLegalName setSignUpStep={setSignUpStep} />} />
                )}
                <Route path="address" element={<SignUpAddress setSignUpStep={setSignUpStep} />} />
                <Route path="contactinfo" element={<SignUpContactInfo setSignUpStep={setSignUpStep} />} />
                <Route path="password" element={<SignUpCreatePassword setSignUpStep={setSignUpStep} />} />
                {isReferralsStepEnabled() && (
                  <Route path="referrals" element={<SignUpReferrals setSignUpStep={setSignUpStep} />} />
                )}
                <Route
                  path="schedule"
                  element={<SignUpSchedulePayment setSignUpStep={setSignUpStep} subscriptionPlan={subscriptionPlan} />}
                />
                <Route
                  path="aboutyourdata"
                  element={
                    <SignUpAboutYourData setSignUpStep={setSignUpStep} setBypassAuthCheck={setBypassAuthCheck} />
                  }
                />
                <Route
                  path="subscription"
                  element={
                    <SignUpSubscription
                      setSignUpStep={setSignUpStep}
                      setBypassAuthCheck={setBypassAuthCheck}
                      setSubscriptionPlan={setSubscriptionPlan}
                    />
                  }
                />
                <Route
                  path="payment"
                  element={<SignUpPayment subscriptionPlan={subscriptionPlan!} setSignUpStep={setSignUpStep} />}
                />
                <Route
                  path="paymentdate"
                  element={<SignUpChangePaymentDate setSignUpStep={setSignUpStep} signUpStep={signUpStep} />}
                />
                {/* TODO: subscriptionPlan must be in signup context */}
                <Route path="confirmation" element={<SignUpConfirmation setSignUpStep={setSignUpStep} />} />
              </Routes>
            </Col>
          </Row>
          {renderLoginSignUpSwitch()}
          <TrustPilotBox />
        </Container>
      </div>
    </SignUpContextProvider>
  );
};
